import { inject, Injectable } from "@angular/core";
import { RouterStateSnapshot } from "@angular/router";
import { isDefined, isPropertyDefined } from "@recursyve/nice-ts-utils";
import { NiceNavigationItem, NiceNavigationService } from "@recursyve/nice-ui-kit.v2";

import { Accounts } from "../api/nerri/accounts/models/accounts.model";
import { OrganisationAccessModules } from "../api/nerri/organisations/models/organitions-access-modules.model";
import { ContractorNavigationItems } from "./contractor.navigation-items";
import { NerriNavigationItem } from "./models/navigation-items.model";
import { MunicipalityAdminNavigationItems } from "./municipality-admin.navigation-items";
import { ownerNavigation } from "./owner.navigation";

@Injectable({
    providedIn: "root"
})
export class NavigationService {
    private readonly niceNavigationService = inject(NiceNavigationService);

    public init(): void {
        this.niceNavigationService.storeNavigation("owner", ownerNavigation);
    }

    public setNavigationFromAccounts(account: Accounts, modules: OrganisationAccessModules[]): void {
        switch (account.role) {
            case "municipality_admin":
            case "municipality_super_admin":
                this.setMunicipalityNavigation(modules);
                break;
            case "organisation_admin":
            case "organisation_super_admin":
                this.setContractorNavigation(modules);
                break;
            case "owner":
                this.niceNavigationService.setCurrentNavigation("owner");
                break;
            case "technician":
                // for now, the technician does not have access to the platform
                break;
        }
    }

    public canAccessRoute(route: RouterStateSnapshot): boolean {
        const [url] = route.url.split("?");
        if (!isDefined(url)) {
            return false;
        }

        const items = this.niceNavigationService.getCurrentNavigation();
        return this.isRouteAvailable(url, items);
    }

    public setContractorNavigation(modules: OrganisationAccessModules[]): void {
        const navigation: NerriNavigationItem[] = [];
        for (const item of ContractorNavigationItems) {
            if (item.module && !modules.some(module => module.module === item.module)) {
                continue;
            }

            navigation.push(item);
        }

        this.niceNavigationService.storeNavigation("contractor", navigation);
        this.niceNavigationService.setCurrentNavigation("contractor");
    }

    public setMunicipalityNavigation(modules: OrganisationAccessModules[]): void {
        const navigation: NerriNavigationItem[] = [];
        for (const item of MunicipalityAdminNavigationItems) {
            if (isPropertyDefined(item, "children") && item.children.length > 0) {
                const children = item.children.filter((child: NerriNavigationItem) => !isPropertyDefined(child, "module") || modules.some(module => module.module === child.module));
                navigation.push({
                    ...item,
                    children
                });

                continue;
            }

            if (item.module && !modules.some(module => module.module === item.module)) {
                continue;
            }

            navigation.push(item);
        }

        this.niceNavigationService.storeNavigation("municipality", navigation);
        this.niceNavigationService.setCurrentNavigation("municipality");
    }

    private isRouteAvailable(url: string, items: NiceNavigationItem[]): boolean {
        for (const item of items) {
            if (item.link === url) {
                return true;
            }

            if (this.isRouteAvailable(url, item.children ?? [])) {
                return true;
            }
        }

        return false;
    }
}
